import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  Eye, MessageCircle, Phone, TrendingUp, TrendingDown,
  Lock, Users, ShoppingBag, Smartphone, Monitor,
  BarChart3, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

const TrendBadge = ({ current, previous }) => {
  if (!previous || previous === 0) {
    if (current > 0) return <span className="text-xs font-medium text-emerald-500 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" />Nouveau</span>;
    return <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Minus className="w-3 h-3" />--</span>;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return <span className="text-xs font-medium text-emerald-500 flex items-center gap-0.5"><ArrowUpRight className="w-3 h-3" />+{pct}%</span>;
  if (pct < 0) return <span className="text-xs font-medium text-red-500 flex items-center gap-0.5"><ArrowDownRight className="w-3 h-3" />{pct}%</span>;
  return <span className="text-xs text-muted-foreground flex items-center gap-0.5"><Minus className="w-3 h-3" />0%</span>;
};

const CHART_COLORS = ['hsl(var(--primary))', 'hsl(var(--chart-2, 160 60% 45%))'];
const PIE_COLORS = ['#3B82F6', '#8B5CF6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const ShopAnalytics = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('7');

  useEffect(() => {
    fetchAnalytics();
  }, [shopId, days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/shops/${shopId}/analytics?days=${days}`);
      setAnalytics(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setAnalytics(null);
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user?.plan === 'STARTER') {
    return (
      <div className="space-y-6" data-testid="shop-analytics-locked">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Statistiques</h1>
          <p className="text-muted-foreground mt-1">Suivez les performances de votre catalogue</p>
        </div>
        <Card className="text-center py-12">
          <CardContent>
            <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Fonctionnalité PRO</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Les statistiques détaillées sont disponibles à partir du plan PRO.
            </p>
            <Button onClick={() => navigate('/app/billing')}>
              <TrendingUp className="w-4 h-4 mr-2" />Passer au plan PRO
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const prev = analytics?.previousPeriod || {};
  const viewsByDay = analytics?.viewsByDay || [];
  const topProducts = analytics?.topProducts || [];
  const topCategories = analytics?.topCategories || [];
  const device = analytics?.deviceBreakdown || { mobile: 0, desktop: 0 };
  const totalContacts = (analytics?.whatsappClicks || 0) + (analytics?.callClicks || 0);
  const contactRate = analytics?.totalViews ? Math.round((totalContacts / analytics.totalViews) * 100) : 0;
  const prevContacts = (prev.whatsappClicks || 0) + (prev.callClicks || 0);
  const maxProductViews = topProducts.length > 0 ? topProducts[0].views : 1;

  const deviceData = [
    { name: 'Mobile', value: device.mobile },
    { name: 'Desktop', value: device.desktop }
  ].filter(d => d.value > 0);

  const chartData = viewsByDay.map(d => ({
    date: d.date?.slice(5),
    'Visites catalogue': d.pageViews || 0,
    'Vues produits': d.productViews || 0
  }));

  const periodLabel = days === '7' ? '7 jours' : days === '30' ? '30 jours' : '90 jours';

  return (
    <div className="space-y-6" data-testid="shop-analytics">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Statistiques</h1>
          <p className="text-muted-foreground mt-1">Performance sur les {periodLabel}</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="analytics-period-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 derniers jours</SelectItem>
            <SelectItem value="30">30 derniers jours</SelectItem>
            <SelectItem value="90">90 derniers jours</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards - row 1 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          title="Visites catalogue"
          value={analytics?.totalViews || 0}
          icon={<Eye className="h-4 w-4" />}
          subtitle={`~${analytics?.avgViewsPerDay || 0}/jour`}
          trend={<TrendBadge current={analytics?.totalViews || 0} previous={prev.totalViews} />}
          testId="kpi-total-views"
        />
        <KpiCard
          title="Visiteurs uniques"
          value={analytics?.uniqueVisitors || 0}
          icon={<Users className="h-4 w-4" />}
          subtitle="Adresses distinctes"
          trend={<TrendBadge current={analytics?.uniqueVisitors || 0} previous={prev.uniqueVisitors} />}
          testId="kpi-unique-visitors"
        />
        <KpiCard
          title="Vues produits"
          value={analytics?.totalProductViews || 0}
          icon={<ShoppingBag className="h-4 w-4" />}
          subtitle="Fiches produit consultées"
          trend={<TrendBadge current={analytics?.totalProductViews || 0} previous={prev.totalProductViews} />}
          testId="kpi-product-views"
        />
      </div>

      {/* KPI cards - row 2 */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          title="Clics WhatsApp"
          value={analytics?.whatsappClicks || 0}
          icon={<MessageCircle className="h-4 w-4" />}
          subtitle="Demandes de contact"
          trend={<TrendBadge current={analytics?.whatsappClicks || 0} previous={prev.whatsappClicks} />}
          testId="kpi-whatsapp"
        />
        <KpiCard
          title="Clics appel"
          value={analytics?.callClicks || 0}
          icon={<Phone className="h-4 w-4" />}
          subtitle="Appels téléphoniques"
          trend={<TrendBadge current={analytics?.callClicks || 0} previous={prev.callClicks} />}
          testId="kpi-calls"
        />
        <KpiCard
          title="Taux de contact"
          value={`${contactRate}%`}
          icon={<TrendingUp className="h-4 w-4" />}
          subtitle={`${totalContacts} contacts / ${analytics?.totalViews || 0} visites`}
          trend={<TrendBadge current={totalContacts} previous={prevContacts} />}
          testId="kpi-contact-rate"
        />
      </div>

      {/* Area chart - traffic over time */}
      <Card>
        <CardHeader>
          <CardTitle>Trafic par jour</CardTitle>
          <CardDescription>Visites du catalogue et consultations de fiches produit</CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradProducts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[1]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS[1]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} className="text-muted-foreground" />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} className="text-muted-foreground" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="Visites catalogue" stroke={CHART_COLORS[0]} fill="url(#gradViews)" strokeWidth={2} />
                <Area type="monotone" dataKey="Vues produits" stroke={CHART_COLORS[1]} fill="url(#gradProducts)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={<BarChart3 />} text="Pas encore de données de trafic" />
          )}
        </CardContent>
      </Card>

      {/* Bottom row: Top products + sidebar (Device + Categories) */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Top products - takes 2 cols */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top 10 produits consultés</CardTitle>
            <CardDescription>Les fiches produit les plus vues sur la période</CardDescription>
          </CardHeader>
          <CardContent>
            {topProducts.length > 0 ? (
              <div className="space-y-3">
                {topProducts.map((product, index) => (
                  <div key={product.productId} className="flex items-center gap-3" data-testid={`top-product-${index}`}>
                    <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-sm truncate">{product.name}</p>
                        <span className="text-sm font-semibold tabular-nums ml-2">{product.views}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all"
                            style={{ width: `${(product.views / maxProductViews) * 100}%` }}
                          />
                        </div>
                        {product.categoryName && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">{product.categoryName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState icon={<ShoppingBag />} text="Aucune consultation de produit" />
            )}
          </CardContent>
        </Card>

        {/* Sidebar: Device + Categories */}
        <div className="space-y-6">
          {/* Device breakdown */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Appareils</CardTitle>
            </CardHeader>
            <CardContent>
              {deviceData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={deviceData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} paddingAngle={4} dataKey="value">
                          {deviceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" style={{ color: PIE_COLORS[0] }} />Mobile</span>
                      <span className="font-semibold">{device.mobile} <span className="text-muted-foreground font-normal">({deviceData.length > 0 ? Math.round((device.mobile / (device.mobile + device.desktop)) * 100) : 0}%)</span></span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5" style={{ color: PIE_COLORS[1] }} />Desktop</span>
                      <span className="font-semibold">{device.desktop} <span className="text-muted-foreground font-normal">({deviceData.length > 0 ? Math.round((device.desktop / (device.mobile + device.desktop)) * 100) : 0}%)</span></span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Pas encore de données</p>
              )}
            </CardContent>
          </Card>

          {/* Top categories */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Top catégories</CardTitle>
            </CardHeader>
            <CardContent>
              {topCategories.length > 0 ? (
                <div className="space-y-3">
                  {topCategories.map((cat, i) => (
                    <div key={cat.categoryId} className="flex items-center justify-between text-sm" data-testid={`top-category-${i}`}>
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        {cat.name}
                      </span>
                      <span className="font-semibold tabular-nums">{cat.views} vues</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Pas encore de données</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ title, value, icon, subtitle, trend, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      <span className="text-muted-foreground">{icon}</span>
    </CardHeader>
    <CardContent>
      <div className="flex items-end justify-between">
        <div className="text-2xl font-bold">{value}</div>
        {trend}
      </div>
      <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
    </CardContent>
  </Card>
);

const EmptyState = ({ icon, text }) => (
  <div className="h-48 flex items-center justify-center text-muted-foreground">
    <div className="text-center">
      <div className="w-12 h-12 mx-auto mb-2 opacity-50">{icon}</div>
      <p className="text-sm">{text}</p>
    </div>
  </div>
);

export default ShopAnalytics;
